import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Send } from "lucide-react";
import { SITE } from "@/data/index";
import { springPresets } from "@/lib/motion";

/* @section: contact-hero */
function ContactHero() {
  return (
    <section className="py-28 bg-background border-b border-border">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={springPresets.gentle}
          className="max-w-2xl"
        >
          <span className="text-xs text-primary tracking-[0.3em] uppercase mb-6 block">Contact</span>
          <h1
            className="text-4xl md:text-5xl font-semibold text-foreground leading-[1.3] mb-6"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            お問い合わせ
          </h1>
          <div className="w-12 h-px bg-primary mb-6" />
          <p className="text-muted-foreground text-lg leading-[1.9]">
            コーチングや研修のご相談、お見積りなど、お気軽にご連絡ください。
            初回のご相談は無料で対応しています。
          </p>
        </motion.div>
      </div>
    </section>
  );
}

// Formspree Form ID — replace with your actual ID from formspree.io
const FORMSPREE_ID = "REPLACE_WITH_YOUR_ID";

/* @section: contact-form */
function ContactForm() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    service: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      const res = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError("送信に失敗しました。しばらくしてから再度お試しください。");
      }
    } catch {
      setError("ネットワークエラーが発生しました。再度お試しください。");
    } finally {
      setSending(false);
    }
  };

  const inputClass =
    "w-full border border-input bg-background text-foreground text-sm px-4 py-3 rounded-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors placeholder:text-muted-foreground/60";

  return (
    <section className="py-24 bg-background">
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-5 gap-14">
          {/* Info */}
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
            className="md:col-span-2 flex flex-col gap-8"
          >
            <div>
              <h2
                className="text-xl font-semibold text-foreground mb-4"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                ご相談の流れ
              </h2>
              <ol className="flex flex-col gap-5">
                {[
                  { step: "01", text: "フォームよりお問い合わせ" },
                  { step: "02", text: "担当者よりメールにてご連絡（1〜2営業日以内）" },
                  { step: "03", text: "初回無料ヒアリング（30〜45分）" },
                  { step: "04", text: "プログラム設計・お見積り" },
                ].map((item) => (
                  <li key={item.step} className="flex items-start gap-4">
                    <span
                      className="text-xs text-primary"
                      style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                    >
                      {item.step}
                    </span>
                    <span className="text-sm text-muted-foreground leading-relaxed">{item.text}</span>
                  </li>
                ))}
              </ol>
            </div>
            <div className="border border-border rounded-sm p-6">
              <div className="flex items-center gap-3 mb-3">
                <Mail size={16} className="text-primary" />
                <span className="text-sm font-medium text-foreground">メールでのお問い合わせ</span>
              </div>
              <a
                href={`mailto:${SITE.email}`}
                className="text-sm text-primary hover:underline"
              >
                {SITE.email}
              </a>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: 16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
            className="md:col-span-3"
          >
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full min-h-[300px] text-center gap-4">
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                  <Send size={24} className="text-primary" />
                </div>
                <h3
                  className="text-xl font-semibold text-foreground"
                  style={{ fontFamily: "'Noto Serif JP', serif" }}
                >
                  送信完了しました
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed max-w-sm">
                  お問い合わせありがとうございます。<br />
                  1〜2営業日以内にご連絡いたします。
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs text-muted-foreground tracking-wider" style={{ letterSpacing: "0.06em" }}>
                      お名前 <span className="text-primary">*</span>
                    </label>
                    <input
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      placeholder="山田 太郎"
                      className={inputClass}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs text-muted-foreground tracking-wider" style={{ letterSpacing: "0.06em" }}>
                      会社名・組織名
                    </label>
                    <input
                      name="company"
                      value={form.company}
                      onChange={handleChange}
                      placeholder="株式会社〇〇"
                      className={inputClass}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs text-muted-foreground tracking-wider" style={{ letterSpacing: "0.06em" }}>
                    メールアドレス <span className="text-primary">*</span>
                  </label>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    placeholder="example@company.com"
                    className={inputClass}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs text-muted-foreground tracking-wider" style={{ letterSpacing: "0.06em" }}>
                    ご関心のサービス
                  </label>
                  <select
                    name="service"
                    value={form.service}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="">選択してください</option>
                    <option>パフォーマンスコーチング</option>
                    <option>リーダーシップ研修</option>
                    <option>AI×ビジネス人材育成</option>
                    <option>組織実行力向上研修</option>
                    <option>その他・複数のサービス</option>
                  </select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs text-muted-foreground tracking-wider" style={{ letterSpacing: "0.06em" }}>
                    お問い合わせ内容 <span className="text-primary">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder="ご相談内容をご記入ください"
                    className={inputClass + " resize-none"}
                  />
                </div>
                <div className="flex flex-col gap-3 mt-2">
                  {error && (
                    <p className="text-sm text-red-500">{error}</p>
                  )}
                  <button
                    type="submit"
                    disabled={sending}
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary text-primary-foreground text-sm tracking-wider hover:opacity-90 transition-opacity rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ letterSpacing: "0.1em" }}
                  >
                    {sending ? "送信中..." : "送信する"}
                    {!sending && <Send size={14} />}
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default function Contact() {
  return (
    <>
      <ContactHero />
      <ContactForm />
    </>
  );
}
